import {
  AlignHorizontalJustifyCenter,
  AlignHorizontalJustifyEnd,
  AlignHorizontalJustifyStart,
  AlignHorizontalSpaceBetween,
  AlignVerticalJustifyCenter,
  AlignVerticalJustifyEnd,
  AlignVerticalJustifyStart,
  AlignVerticalSpaceBetween,
  type LucideIcon,
} from "lucide-react";
import type { SceneNode } from "@/document/model/nodes";
import type { Rect } from "@/lib/math/rect";
import { textVisualBounds } from "@/renderer/text/textVisualBounds";
import {
  alignmentLabels,
  getAlignmentUpdates,
  getDistributionUpdates,
  type AlignmentAction,
} from "./alignment";
import { PanelSection } from "./PanelSection";

type DistributionAction = "horizontal" | "vertical";

interface AlignmentSectionProps {
  nodes: SceneNode[];
  referenceBounds?: Rect;
  onBoundsChange: (updates: Array<{ nodeId: string; bounds: Rect }>, label: string) => void;
}

const alignmentActions: Array<{ action: AlignmentAction; label: string; icon: LucideIcon }> = [
  { action: "left", label: alignmentLabels.left, icon: AlignHorizontalJustifyStart },
  { action: "horizontalCenter", label: alignmentLabels.horizontalCenter, icon: AlignHorizontalJustifyCenter },
  { action: "right", label: alignmentLabels.right, icon: AlignHorizontalJustifyEnd },
  { action: "top", label: alignmentLabels.top, icon: AlignVerticalJustifyStart },
  { action: "verticalCenter", label: alignmentLabels.verticalCenter, icon: AlignVerticalJustifyCenter },
  { action: "bottom", label: alignmentLabels.bottom, icon: AlignVerticalJustifyEnd },
];

const distributionActions: Array<{ action: DistributionAction; label: string; icon: LucideIcon }> = [
  { action: "horizontal", label: "Distribute horizontal spacing", icon: AlignHorizontalSpaceBetween },
  { action: "vertical", label: "Distribute vertical spacing", icon: AlignVerticalSpaceBetween },
];

function textVisualBoundsOverrides(nodes: SceneNode[]): Map<string, Rect> {
  const overrides = new Map<string, Rect>();
  for (const node of nodes) {
    if (node.type !== "text") {
      continue;
    }
    overrides.set(node.id, textVisualBounds(node));
  }
  return overrides;
}

export function AlignmentSection({ nodes, referenceBounds, onBoundsChange }: AlignmentSectionProps) {
  const canAlign = nodes.length > 0 && referenceBounds !== undefined;
  const canDistribute = nodes.length > 2;

  return (
    <PanelSection title="Align">
      <div className="grid gap-2">
        <div className="grid grid-cols-6 overflow-hidden rounded bg-white/[0.04]">
        {alignmentActions.map(({ action, label, icon: Icon }) => (
          <button
            key={action}
            type="button"
            title={label}
            aria-label={label}
            disabled={!canAlign}
            onClick={() => {
              if (referenceBounds) {
                onBoundsChange(getAlignmentUpdates(nodes, referenceBounds, action, textVisualBoundsOverrides(nodes)), label);
              }
            }}
            className="flex h-8 items-center justify-center text-slate-300 transition-colors hover:bg-white/[0.08] hover:text-slate-100 disabled:pointer-events-none disabled:text-slate-600"
          >
            <Icon className="size-4" />
          </button>
        ))}
        </div>

        <div className="grid grid-cols-2 overflow-hidden rounded bg-white/[0.04]">
        {distributionActions.map(({ action, label, icon: Icon }) => (
          <button
            key={action}
            type="button"
            title={label}
            aria-label={label}
            disabled={!canDistribute}
            onClick={() => onBoundsChange(getDistributionUpdates(nodes, action), label)}
            className="flex h-8 items-center justify-center gap-2 text-slate-300 transition-colors hover:bg-white/[0.08] hover:text-slate-100 disabled:pointer-events-none disabled:text-slate-600"
          >
            <Icon className="size-4" />
            <span>{action === "horizontal" ? "Horizontal" : "Vertical"}</span>
          </button>
        ))}
        </div>
      </div>
    </PanelSection>
  );
}
